// Client-side greeting audio: the server's strict canonical-WAV check (apps/api/src/voicemail/wav.ts, same rules and
// codes) so a file is judged before upload, and the conversion of decoded or recorded audio to that canonical form:
// PCM 16-bit, mono, 8000 Hz, 1 to 60 s, at most 1 MiB. The server still decides; nothing here is trusted by it.
// Pure functions on typed arrays: no DOM, so they run (and are tested) anywhere.
import { LIMITS } from './model.ts';

export type GreetingProblem = 'greeting_invalid_wav' | 'greeting_format_unsupported' | 'greeting_too_short' | 'greeting_too_long' | 'greeting_too_large';
export type WavCheck = { ok: true; bytes: number; durationMs: number; dataBytes: number } | { ok: false; code: GreetingProblem };

const RATE = LIMITS.sampleRate;
const BYTE_RATE = RATE * 2;
const ascii = (b: Uint8Array, at: number) => String.fromCharCode(b[at] ?? 0, b[at + 1] ?? 0, b[at + 2] ?? 0, b[at + 3] ?? 0);

/** Same decision as the server's checkGreetingWav, from the RIFF header and the byte length only. */
export function checkGreetingWav(body: Uint8Array): WavCheck {
  if (body.length > LIMITS.greetingMaxBytes) return { ok: false, code: 'greeting_too_large' };
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  if (body.length < 44 || ascii(body, 0) !== 'RIFF' || ascii(body, 8) !== 'WAVE' || view.getUint32(4, true) + 8 !== body.length) return { ok: false, code: 'greeting_invalid_wav' };
  let position = 12, format = false, list = false;
  let data: { offset: number; bytes: number } | null = null;
  for (let chunks = 0; position < body.length; chunks++) {
    if (chunks >= 8 || position + 8 > body.length) return { ok: false, code: 'greeting_invalid_wav' };
    const name = ascii(body, position), size = view.getUint32(position + 4, true), start = position + 8;
    if (start + size > body.length) return { ok: false, code: 'greeting_invalid_wav' };
    if (name === 'fmt ') {
      if (format || data || size !== 16) return { ok: false, code: size !== 16 && !format ? 'greeting_format_unsupported' : 'greeting_invalid_wav' };
      if (view.getUint16(start, true) !== 1 || view.getUint16(start + 2, true) !== 1 || view.getUint32(start + 4, true) !== RATE || view.getUint32(start + 8, true) !== BYTE_RATE || view.getUint16(start + 12, true) !== 2 || view.getUint16(start + 14, true) !== 16) {
        return { ok: false, code: 'greeting_format_unsupported' };
      }
      format = true;
    } else if (name === 'LIST') {
      if (list || !format || data || size > 4096) return { ok: false, code: 'greeting_invalid_wav' };
      list = true;
    } else if (name === 'data') {
      if (!format || data) return { ok: false, code: 'greeting_invalid_wav' };
      data = { offset: start, bytes: size };
    } else return { ok: false, code: 'greeting_invalid_wav' };
    position = start + size + (size % 2);
  }
  if (!format || !data || position !== body.length || data.offset + data.bytes + (data.bytes % 2) !== body.length) return { ok: false, code: 'greeting_invalid_wav' };
  if (data.bytes === 0 || data.bytes % 2) return { ok: false, code: 'greeting_invalid_wav' };
  const durationMs = Math.floor((data.bytes * 1000) / BYTE_RATE);
  if (durationMs < LIMITS.greetingMinMs) return { ok: false, code: 'greeting_too_short' };
  if (durationMs > LIMITS.greetingMaxMs) return { ok: false, code: 'greeting_too_long' };
  return { ok: true, bytes: body.length, durationMs, dataBytes: data.bytes };
}

/** Averages channels into one (equal weight). An empty list gives an empty signal. */
export function mixToMono(channels: readonly Float32Array[]): Float32Array {
  const first = channels[0];
  if (!first) return new Float32Array(0);
  if (channels.length === 1) return first;
  const length = Math.min(...channels.map(c => c.length));
  const out = new Float32Array(length);
  for (const channel of channels) for (let i = 0; i < length; i++) out[i]! += channel[i]! / channels.length;
  return out;
}

const HALF_ZEROS = 8; // zero crossings each side of the low-pass kernel
const TABLE_STEPS = 64;

/**
 * Band-limited resampling (windowed sinc, Blackman window). Downsampling low-passes at 0.9 of the new Nyquist
 * frequency first, so 48 kHz or 44.1 kHz microphone audio doesn't alias into the 8 kHz greeting.
 */
export function resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (!(fromRate > 0) || !(toRate > 0) || !Number.isFinite(fromRate) || !Number.isFinite(toRate)) throw new RangeError('sample rates must be positive');
  if (fromRate === toRate) return input.slice();
  const ratio = fromRate / toRate;
  const outLength = Math.floor((input.length * toRate) / fromRate);
  const out = new Float32Array(outLength);
  const cutoff = Math.min(1, toRate / fromRate) * 0.9; // in cycles per input sample, times 2 (1 = input Nyquist)
  const half = Math.ceil(HALF_ZEROS / cutoff);
  // The kernel is tabulated once (TABLE_STEPS points per input sample) and read with linear interpolation.
  const table = new Float32Array(half * TABLE_STEPS + 2);
  for (let i = 0; i < table.length; i++) {
    const x = i / TABLE_STEPS;
    if (x >= half) break;
    const sinc = x === 0 ? 1 : Math.sin(Math.PI * cutoff * x) / (Math.PI * cutoff * x);
    table[i] = sinc * (0.42 + 0.5 * Math.cos((Math.PI * x) / half) + 0.08 * Math.cos((2 * Math.PI * x) / half));
  }
  for (let n = 0; n < outLength; n++) {
    const center = n * ratio;
    const lo = Math.max(0, Math.ceil(center - half)), hi = Math.min(input.length - 1, Math.floor(center + half));
    let sum = 0, weight = 0;
    for (let k = lo; k <= hi; k++) {
      const a = Math.abs(k - center) * TABLE_STEPS, i = Math.floor(a), f = a - i;
      const tap = (table[i] ?? 0) * (1 - f) + (table[i + 1] ?? 0) * f;
      sum += input[k]! * tap;
      weight += tap;
    }
    out[n] = weight > 0 ? sum / weight : 0;
  }
  return out;
}

/** Canonical WAV bytes from 8 kHz mono samples in [-1, 1] (clamped, rounded to 16-bit). */
export function encodeGreetingWav(samples: Float32Array): Uint8Array<ArrayBuffer> {
  const dataBytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const write = (at: number, text: string) => { for (let i = 0; i < text.length; i++) view.setUint8(at + i, text.charCodeAt(i)); };
  write(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, RATE, true);
  view.setUint32(28, BYTE_RATE, true);
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  write(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, Number.isFinite(samples[i]!) ? samples[i]! : 0));
    view.setInt16(44 + i * 2, Math.round(s < 0 ? s * 32768 : s * 32767), true);
  }
  return new Uint8Array(buffer);
}

export type Conversion =
  | { ok: true; wav: Uint8Array<ArrayBuffer>; durationMs: number }
  | { ok: false; code: 'greeting_too_short' | 'greeting_too_long' | 'no_audio' };

/** Decoded audio (any rate, any channel count) to a canonical greeting, refused before upload when out of bounds. */
export function canonicalGreeting(channels: readonly Float32Array[], sampleRate: number): Conversion {
  const mono = mixToMono(channels);
  if (mono.length === 0 || !(sampleRate > 0)) return { ok: false, code: 'no_audio' };
  const durationMs = Math.floor((mono.length * 1000) / sampleRate);
  if (durationMs < LIMITS.greetingMinMs) return { ok: false, code: 'greeting_too_short' };
  if (durationMs > LIMITS.greetingMaxMs) return { ok: false, code: 'greeting_too_long' };
  const pcm = resample(mono, sampleRate, RATE);
  let peak = 0;
  for (let i = 0; i < pcm.length; i++) peak = Math.max(peak, Math.abs(pcm[i]!));
  if (peak < 1 / 32768) return { ok: false, code: 'no_audio' };
  const wav = encodeGreetingWav(pcm);
  const check = checkGreetingWav(wav);
  if (!check.ok) return { ok: false, code: check.code === 'greeting_too_short' ? 'greeting_too_short' : 'greeting_too_long' };
  return { ok: true, wav, durationMs: check.durationMs };
}

/** Plain copy for a client-side refusal or conversion problem (the server's codes plus our own). */
export const PREPARE_COPY: Readonly<Record<string, string>> = Object.freeze({
  greeting_invalid_wav: 'This file isn’t a readable WAV recording, and this browser couldn’t convert it.',
  greeting_format_unsupported: 'This WAV file isn’t in the phone format (8 kHz, 16-bit, mono), and this browser couldn’t convert it.',
  greeting_too_short: 'The greeting must be at least 1 second long.',
  greeting_too_long: 'The greeting can be at most 60 seconds long. Trim it, then try again.',
  greeting_too_large: 'The greeting file can be at most 1 MiB.',
  no_audio: 'No sound was found in this audio. Check the microphone or the file, then try again.',
  not_audio: 'This file couldn’t be read as audio. Choose a WAV file (8 kHz, 16-bit, mono), or another audio file this browser can play.',
  file_too_big: 'This file is too large to convert here. Choose a file under 50 MB.',
});
export const MAX_SOURCE_BYTES = 50 * 1024 * 1024;
